import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, Zap, Users } from 'lucide-react';

const classes = [
  {
    name: 'HYROX',
    duration: '45 min',
    intensity: 'High',
    maxParticipants: 20,
    description: 'High-intensity functional training combining strength, cardio, and dynamic movements for maximum performance.',
    trainer: 'Rebekka Schulze',
    color: 'from-blue-500/20 to-cyan-500/20'
  },
  {
    name: 'PILATES',
    duration: '60 min',
    intensity: 'Medium',
    maxParticipants: 15,
    description: 'Build core strength, improve flexibility, and enhance posture through controlled, precise movements.',
    trainer: 'Katelyn',
    color: 'from-cyan-500/20 to-teal-500/20'
  },
  {
    name: 'YOGA',
    duration: '45 min',
    intensity: 'Low-Medium',
    maxParticipants: 20,
    description: 'A guided session where participants practice a series of movements, stretches, breathing exercises, and meditation to improve flexibility, strength, balance, and relaxation.',
    trainer: 'Katelyn',
    color: 'from-purple-500/20 to-pink-500/20'
  },
  {
    name: 'STRENGTH CLUB',
    duration: '60 min',
    intensity: 'High',
    maxParticipants: 18,
    description: 'Full-body resistance training focusing on proper form and progressive overload.',
    trainer: 'Miriam',
    color: 'from-emerald-500/20 to-blue-500/20'
  }
];

export default function Classes() {
  const [hoveredClass, setHoveredClass] = useState<number | null>(null);

  return (
    <section id="classes" className="py-20 bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-7xl font-bold mb-4">GROUP FITNESS</h2>
          <p className="text-xl text-gray-400">Transform together with our signature classes</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {classes.map((classItem, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.8 }}
              className="relative group"
              onMouseEnter={() => setHoveredClass(index)}
              onMouseLeave={() => setHoveredClass(null)}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${classItem.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`} />
              <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10">
                <div className="p-8 relative">
                  <motion.div
                    animate={{
                      y: hoveredClass === index ? -10 : 0,
                      opacity: hoveredClass === index ? 1 : 0.9,
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex justify-between items-start mb-6">
                      <h3 className="text-4xl font-bold">{classItem.name}</h3>
                    </div>

                    <p className="text-gray-300 mb-6">{classItem.description}</p>

                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center space-x-2">
                        <Clock className="h-5 w-5 text-blue-400" />
                        <span>{classItem.duration}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Zap className="h-5 w-5 text-cyan-400" />
                        <span>{classItem.intensity}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Users className="h-5 w-5 text-teal-400" />
                        <span>Max {classItem.maxParticipants}</span>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-white/10">
                      <p className="text-gray-400">
                        Instructor: <span className="text-white">{classItem.trainer}</span>
                      </p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="text-center mt-16"
        >
          <a 
            href="#contact" 
            className="inline-block bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300"
          >
            BOOK A CLASS
          </a>
        </motion.div>
      </div>
    </section>
  );
}